import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";

export interface ValidationRule<T> {
  field: keyof T;
  validate: (value: unknown, entity: Partial<T>) => string | null;
}

interface UseInlineEditOptions<T> {
  /** Initial entity data */
  entity: T | null | undefined;
  /** Query key to invalidate on save */
  queryKey: unknown[];
  /** Function to save the entity */
  saveFn: (data: Partial<T>) => Promise<unknown>;
  /** Validation rules */
  validationRules?: ValidationRule<T>[];
  /** Fields to include in the editable form */
  editableFields: (keyof T)[];
  /** Optional: Transform field values before setting (e.g., lowercase source) */
  fieldTransformers?: Partial<Record<keyof T, (value: unknown) => unknown>>;
}

interface UseInlineEditReturn<T> {
  isEditMode: boolean;
  editedEntity: Partial<T>;
  isDirty: boolean;
  validationErrors: Record<string, string>;
  isSaving: boolean;
  handleStartEdit: () => void;
  handleCancelEdit: () => void;
  handleFieldChange: (field: keyof T, value: unknown) => void;
  handleSaveEdit: () => void;
}

/**
 * Custom hook for inline edit mode functionality
 *
 * @example
 * const {
 *   isEditMode,
 *   editedEntity,
 *   isDirty,
 *   validationErrors,
 *   isSaving,
 *   handleStartEdit,
 *   handleCancelEdit,
 *   handleFieldChange,
 *   handleSaveEdit,
 * } = useInlineEdit({
 *   entity: candidate,
 *   queryKey: ['candidate', id],
 *   saveFn: (data) => api.recruiting.updateCandidate(id, data),
 *   editableFields: ['first_name', 'last_name', 'email'],
 *   validationRules: [
 *     { field: 'first_name', validate: (v) => !v ? 'Required' : null },
 *   ],
 * });
 */
export function useInlineEdit<T extends object>({
  entity,
  queryKey,
  saveFn,
  validationRules = [],
  editableFields,
  fieldTransformers = {},
}: UseInlineEditOptions<T>): UseInlineEditReturn<T> {
  const queryClient = useQueryClient();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEntity, setEditedEntity] = useState<Partial<T>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Use refs to avoid re-triggering effect when options change
  const editableFieldsRef = useRef(editableFields);
  const fieldTransformersRef = useRef(fieldTransformers);
  editableFieldsRef.current = editableFields;
  fieldTransformersRef.current = fieldTransformers;

  // Initialize editedEntity when entity data loads
  useEffect(() => {
    if (entity && !isEditMode) {
      const initialData: Partial<T> = {};
      for (const field of editableFieldsRef.current) {
        const value = entity[field];
        const transformer = fieldTransformersRef.current[field];
        initialData[field] = (
          transformer ? transformer(value) : (value ?? "")
        ) as T[keyof T];
      }
      setEditedEntity(initialData);
    }
  }, [entity, isEditMode]);

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const updateMutation = useMutation(saveFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      setIsEditMode(false);
      setIsDirty(false);
      setValidationErrors({});
    },
    onError: (error: Error) => {
      console.error("Error updating entity:", error);
      setValidationErrors({
        general: "Failed to save changes. Please try again.",
      });
    },
  });

  const handleStartEdit = useCallback(() => {
    if (entity) {
      const initialData: Partial<T> = {};
      for (const field of editableFieldsRef.current) {
        const value = entity[field];
        const transformer = fieldTransformersRef.current[field];
        initialData[field] = (
          transformer ? transformer(value) : (value ?? "")
        ) as T[keyof T];
      }
      setEditedEntity(initialData);
      setIsEditMode(true);
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [entity]);

  const handleCancelEdit = useCallback(() => {
    if (isDirty) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to cancel?",
        )
      ) {
        return;
      }
    }
    setIsEditMode(false);
    setIsDirty(false);
    setValidationErrors({});
  }, [isDirty]);

  const handleFieldChange = useCallback((field: keyof T, value: unknown) => {
    setEditedEntity((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    // Clear validation error for this field
    setValidationErrors((prev) => {
      if (prev[field as string]) {
        const { [field as string]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    for (const rule of validationRules) {
      const error = rule.validate(editedEntity[rule.field], editedEntity);
      if (error) {
        errors[rule.field as string] = error;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [validationRules, editedEntity]);

  const handleSaveEdit = useCallback(() => {
    if (!validate()) {
      return;
    }

    // Convert empty strings to null for optional fields
    const dataToSave: Partial<T> = {};
    for (const field of editableFieldsRef.current) {
      const value = editedEntity[field];
      dataToSave[field] = (value === "" ? null : value) as T[keyof T];
    }

    updateMutation.mutate(dataToSave);
  }, [validate, editedEntity, updateMutation]);

  return {
    isEditMode,
    editedEntity,
    isDirty,
    validationErrors,
    isSaving: updateMutation.isLoading,
    handleStartEdit,
    handleCancelEdit,
    handleFieldChange,
    handleSaveEdit,
  };
}

// Common validation rules factory functions
export const required = <T>(
  field: keyof T,
  message = "This field is required",
): ValidationRule<T> => ({
  field,
  validate: (value) => (!value?.toString().trim() ? message : null),
});

export const email = <T>(
  field: keyof T,
  message = "Email is invalid",
): ValidationRule<T> => ({
  field,
  validate: (value) => {
    const str = typeof value === "string" ? value.trim() : "";
    if (!str) return null; // Let required rule handle empty
    return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(str) ? null : message;
  },
});

export const url = <T>(
  field: keyof T,
  message = "URL must start with http:// or https://",
): ValidationRule<T> => ({
  field,
  validate: (value) => {
    const str = typeof value === "string" ? value.trim() : "";
    if (!str) return null; // Let required rule handle empty
    return str.startsWith("http") ? null : message;
  },
});

export const numeric = <T>(
  field: keyof T,
  message = "Must be a number",
): ValidationRule<T> => ({
  field,
  validate: (value) => {
    if (value === null || value === undefined || value === "") return null;
    return Number.isNaN(Number(value)) ? message : null;
  },
});

export default useInlineEdit;
